import { Button, Select, Tooltip } from 'flowbite-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiArrowLeft, FiSearch, FiPlus, FiUpload } from 'react-icons/fi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';
import { apiEndPoints } from '../../../hooks/remote/apiEndPoints';
import { toast } from 'react-toastify';
import { Drawer } from '../../../components/Drawer';
import {
  buildGroups,
  extractFolders,
  inferExtFromMime,
  isFolder,
} from '../../../hooks/constants';
import CardSkeleton from '../../../components/Skeletons/CardSkeleton';
import { BsInfoCircle } from 'react-icons/bs';

function MetaRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900">{value}</dd>
    </div>
  );
}

export default function BucketPage() {
  const { id: projectId, id2: bucketName } = useParams();
  const location = useLocation();
  const { state } = location;
  const [objects, setObjects] = useState([]);
  const [rows, setRows] = useState(() => buildGroups(objects));
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // string key
  const [isDeleting, setIsDeleting] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [previewKey, setPreviewKey] = useState(null);
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewMime, setPreviewMime] = useState(null);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [openCreateFolder, setOpenCreateFolder] = useState(false);
  const [openChooseFile, setOpenChooseFile] = useState(false);
  const [previewFilename, setPreviewFilename] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const [openKey, setOpenKey] = useState(null);
  const [folderList, setFolderList] = useState([]); // list of folder keys
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [isFolderName, setIsFolderName] = useState(false);
  const [folderName, setFolderName] = useState(null);
  const [tierDetail, setTierDetail] = useState(null);
  const [totalUsage, setTotalUsage] = useState(null);

  const bucketMeta = useMemo(
    () => ({
      Name: bucketName,
      'Size of the Bucket': tierDetail?.storage_size
        ? `${tierDetail.storage_size} GB`
        : 'N/A',
      'The local size used': totalUsage || '0.00 GB',
      'Tier Details': tierDetail?.name || 'N/A',
      Objects: objects.length,
      Created: new Date(state.created).toLocaleString(),
      Visibility: state.public ? 'Public' : 'Private',
      Versioning: 'N/A',
    }),
    [
      bucketName,
      objects.length,
      state.created,
      state.public,
      tierDetail,
      totalUsage,
    ],
  );
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = ''; // allow re-selecting same files
  };

  const handleShowAddFolder = () => {
    setShowAddDrawer(true);
    setOpenCreateFolder(true);
  };
  const handleShowAddFile = () => {
    setShowAddDrawer(true);
    setOpenChooseFile(true);
  };
  const handleFetchBucketContents = useCallback(async () => {
    try {
      const res = await apiEndPoints.fetchBucketStorageObjects(
        projectId,
        bucketName,
      );
      const objects = res.data.results.map((object) => {
        return {
          key: object.name,
          size: object.size,
          contentType: object.content_type,
          lastModified: object.last_modified,
          isFolder: object.is_folder,
          children: object?.children || [],
        };
      });
      setObjects(objects);
      setRows(objects);
      setFolderList(extractFolders(objects));
      setTierDetail(res.data.tier_detail);
      setTotalUsage(res.data.total_usage);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error fetching objects');
    }
  }, [projectId, bucketName]);

  const handleUpload = async (e) => {
    if (selectedFiles.length === 0 || uploading) return;
    const formData = new FormData();
    if (selectedFiles.length === 1) {
      formData.append('file', selectedFiles[0], selectedFiles[0].name);
    } else {
      selectedFiles.forEach((file) => {
        formData.append('files', file, file.name);
      });
    }

    setUploading(true);
    try {
      let res;
      if (selectedFolder) {
        // Note: The backend might need an update for multiple files in a folder too,
        // but for now we follow the pattern for root upload.
        res = await apiEndPoints.addFileToFolder(
          projectId,
          bucketName,
          selectedFolder,
          formData,
        );
      } else {
        if (selectedFiles.length > 1) {
          res = await apiEndPoints.uploadObjects(
            projectId,
            bucketName,
            formData,
          );
        } else {
          res = await apiEndPoints.uploadObject(
            projectId,
            bucketName,
            formData,
          );
        }
      }

      setProgress(100);
      toast.success(res.data.message);
      handleFetchBucketContents();
      handleCloseAddDrawer();
    } catch (error) {
      const status = error?.response?.status;
      if (status === 413) {
        toast.error(
          'File is too large to upload. Please choose a smaller file and try again.',
        );
      } else if (status === 401 || status === 403) {
        toast.error('Not authorized to perform this action');
      } else {
        toast.error(error?.response?.data?.message || 'Error uploading object');
      }
    } finally {
      setUploading(false);
    }
  };
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName) return;
    setCreatingFolder(true);

    try {
      await apiEndPoints.createFolder(projectId, bucketName, newFolderName);
      setNewFolderName('');
      setOpenCreateFolder(false);
      handleFetchBucketContents();
      setShowAddDrawer(false);
      toast.success('Folder created successfully');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error creating folder');
    } finally {
      setCreatingFolder(false);
    }
  };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const res = folderName
        ? await apiEndPoints.deleteFileFromFolder(
            projectId,
            bucketName,
            folderName,
            deleteTarget,
          )
        : await apiEndPoints.deleteObject(projectId, bucketName, deleteTarget);
      setDeleteTarget(null);
      setFolderName(null);
      toast.success(res.data.message);
      window.location.reload();
    } catch (error) {
      setDeleteTarget(null);
      toast.error(error?.response?.data?.message || 'Error deleting object');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteFolder = async (folderName) => {
    if (!folderName) return;

    setIsDeleting(true);
    try {
      const res = await apiEndPoints.deleteFolder(
        projectId,
        bucketName,
        folderName,
      );
      toast.success(res.data.message);
      window.location.reload();
    } catch (error) {
      setDeleteTarget(null);
      setIsFolderName(false);
      toast.error(error?.response?.data?.message || 'Error deleting folder');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownload = async (key) => {
    if (!key) return;
    setDownloadingKey(key);
    try {
      const res = await apiEndPoints.fetchObject(projectId, bucketName, key);
      const contentType =
        res?.headers?.['content-type'] || 'application/octet-stream';
      const cd = res?.headers?.['content-disposition'];
      let filename = key;
      if (cd) {
        // supports filename* and filename
        const match = cd.match(
          /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i,
        );
        if (match) {
          filename = decodeURIComponent(match[1] || match[2] || filename);
        }
      } else {
        // If no header, infer an extension from content-type
        const ext = inferExtFromMime(contentType);
        if (ext && !filename.includes('.')) {
          filename = `${filename}.${ext}`;
        }
      }
      //  Create Blob using the returned type
      const blob = new Blob([res.data], { type: contentType });

      //  Trigger a download
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (error) {
      const status = error?.response?.status;
      const allow = error?.response?.headers?.allow;
      if (status === 405) {
        const methodHint = allow ? ` Allowed: ${allow}` : '';
        toast.error(
          `Method not allowed. Check the HTTP method for this endpoint.${methodHint}`,
        );
      } else if (status === 404) {
        toast.error('Object not found');
      } else if (status === 401 || status === 403) {
        toast.error('Not authorized to perform this action');
      } else {
        toast.error(
          error?.response?.data?.message || 'Error downloading object',
        );
      }
    } finally {
      setDownloadingKey(null);
    }
  };
  const handlePreviewInDrawer = async (key) => {
    if (!key) return;
    setIsFetchingPreview(true);

    // Revoke the previous preview URL if any
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }

    try {
      const res = await apiEndPoints.previewObject(projectId, bucketName, key);
      const contentType =
        res?.headers?.['content-type'] || 'application/octet-stream';
      const cd = res?.headers?.['content-disposition'];
      let filename = key;
      if (cd) {
        const match = cd.match(
          /filename*=UTF-8''([^;]+)|filename="?([^"]+)"?/i,
        );
        if (match) {
          filename = decodeURIComponent(match[21] || match[22] || filename);
        }
      } else {
        const ext = inferExtFromMime(contentType);
        if (ext && !filename.includes('.')) {
          filename = `${filename}.${ext}`;
        }
      }

      // Normalize to a Blob for preview
      const data = res?.data;
      const blob =
        data instanceof Blob ? data : new Blob([data], { type: contentType });

      const objectUrl = URL.createObjectURL(blob);
      setPreviewUrl(objectUrl);
      setPreviewMime(contentType);
      setPreviewFilename(filename);
    } catch (error) {
      const status = error?.response?.status;
      const allow = error?.response?.headers?.allow;
      if (status === 405) {
        const methodHint = allow ? ` Allowed: ${allow}` : '';
        toast.error(
          `Method not allowed. Check the HTTP method for this endpoint.${methodHint}`,
        );
      } else if (status === 404) {
        toast.error('Object not found');
      } else if (status === 401 || status === 403) {
        toast.error('Not authorized to perform this action');
      } else {
        toast.error(error?.response?.data?.message || 'Error loading preview');
      }
    } finally {
      setIsFetchingPreview(false);
    }
  };

  const handlePreview = async (key) => {
    setPreviewKey(key);
    handlePreviewInDrawer(key);
  };

  const handleCloseAddDrawer = () => {
    setShowAddDrawer(false);
    setOpenCreateFolder(false);
    setOpenChooseFile(false);
    setSelectedFiles([]);
    setNewFolderName('');
    setSelectedFolder(null);
  };

  useEffect(() => {
    handleFetchBucketContents();
  }, [handleFetchBucketContents]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="border-0 text-primaryBlue cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <FiArrowLeft size={20} />
          </Button>
          <p className="crimson-font text-lg font-medium text-black dark:text-white sm:text-xl">
            Bucket: {bucketName}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative w-full sm:w-64">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <FiSearch className="w-4 h-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-primaryBlue focus:border-primaryBlue dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 outline-none transition-all"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              color="info"
              className="!bg-primaryBlue hover:!bg-blue-600 text-white border-0"
              onClick={handleShowAddFile}
            >
              <FiUpload className="mr-2 h-4 w-4" />
              <span className="whitespace-nowrap">Add File</span>
            </Button>
            <Button
              size="sm"
              color="info"
              className="!bg-primaryBlue hover:!bg-blue-600 text-white border-0"
              onClick={handleShowAddFolder}
            >
              <FiPlus className="mr-2 h-4 w-4" />
              <span className="whitespace-nowrap">Add Folder</span>
            </Button>
          </div>
        </div>
      </div>
      {/* Progress bar (only visible during upload) */}
      {uploading && (
        <div className="mb-4 rounded-lg border bg-white p-3">
          <p className="mb-2 text-sm text-gray-700">
            Uploading {selectedFiles.length} file(s) •{' '}
            {(selectedFiles.reduce((acc, f) => acc + f.size, 0) / 1024).toFixed(
              1,
            )}{' '}
            KB
          </p>
          <div className="h-2 w-full overflow-hidden rounded bg-gray-100">
            <div
              className="h-full bg-primaryBlue transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Metadata */}
      <section className="mb-6 rounded-lg border bg-white p-4">
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(bucketMeta).map(([k, v]) => (
            <MetaRow key={k} label={k} value={String(v)} />
          ))}
        </dl>
      </section>

      {/* Objects list or empty */}
      {objects.length === 0 ? (
        <div className="flex h-56 flex-col items-center justify-center rounded-lg border border-dashed bg-white text-center">
          <p className="text-sm text-gray-600">
            No objects. Use the "Add File" button to add files.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Size
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Type
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Last modified
                </th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows
                .filter((o) => {
                  const name = o.name ?? o.key;
                  return name.toLowerCase().includes(searchQuery.toLowerCase());
                })
                .map((o) => {
                  const name = o.name ?? o.key;
                  const folder = isFolder(name);
                  if (!folder) {
                    return (
                      <tr key={name} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-primaryBlue">
                          <button
                            className="truncate underline decoration-transparent hover:decoration-inherit"
                            onClick={() => {
                              handlePreview(name);
                            }}
                            title={name}
                          >
                            {name}
                          </button>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {o.size}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {o.contentType?.split('/')[1] || 'folder'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {new Date(o.lastModified).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleDownload(name)}
                              className="rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50"
                            >
                              {downloadingKey === name
                                ? 'Downloading...'
                                : 'Download'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(name)}
                              className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                            >
                              {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  const base = name.replace(/\/$/, '');
                  const children = o.children || [];

                  return (
                    // Fragment pairs the main folder row with its expandable panel row
                    <tbody key={name} className="contents">
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-primaryBlue">
                          <Disclosure as="div">
                            {({ open }) => (
                              <>
                                <DisclosureButton
                                  onClick={() => setOpenKey(open ? null : name)}
                                  className="flex w-full items-center gap-2 underline decoration-transparent hover:decoration-inherit"
                                  title={name}
                                >
                                  <span
                                    className={`transition-transform ${openKey === name ? 'rotate-90' : ''}`}
                                  >
                                    ▸
                                  </span>
                                  <span className="truncate">{base}</span>
                                </DisclosureButton>
                                {/* Invisible panel placeholder to control state; the visible content is the next row */}
                                <DisclosurePanel className="hidden" />
                              </>
                            )}
                          </Disclosure>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {o.size || '0.0 MB'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          folder
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {new Date(o.lastModified).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setIsFolderName(true);
                                setDeleteTarget(name);
                              }}
                              className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                            >
                              {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expandable detail row spanning all columns */}
                      {openKey === name && (
                        <tr>
                          <td colSpan={5} className="bg-gray-50 px-4 py-3">
                            {children.length === 0 ? (
                              <div className="text-sm text-gray-500">
                                Empty folder
                              </div>
                            ) : (
                              <ul className="max-h-[200px] divide-y divide-gray-200 overflow-y-auto ">
                                {children.map((child) => {
                                  const childName = child.name ?? child.key;
                                  const display = childName.replace(name, '');
                                  return (
                                    <li key={childName} className="py-2 ml-4 ">
                                      <div className="flex items-center justify-between">
                                        <button
                                          type="button"
                                          className="truncate text-sm text-primaryBlue underline decoration-transparent hover:decoration-inherit cursor-not-allowed"
                                          onClick={() => {
                                            //handlePreview(childName)
                                          }}
                                          title={childName}
                                        >
                                          {display}
                                        </button>
                                        <div className="ml-4 flex items-center gap-3">
                                          <span className="text-sm text-gray-500">
                                            {child.size}
                                          </span>
                                          <span className="text-sm text-gray-500">
                                            {child.content_type?.split(
                                              '/',
                                            )[1] || 'folder'}
                                          </span>
                                          <span className="text-sm text-gray-500">
                                            {new Date(
                                              child.last_modified,
                                            ).toLocaleString()}
                                          </span>

                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleDownload(childName)
                                            }
                                            className="rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-100 disabled:opacity-50"
                                          >
                                            {downloadingKey === childName
                                              ? 'Downloading...'
                                              : 'Download'}
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setFolderName(base);
                                              setDeleteTarget(childName);
                                            }}
                                            className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50 "
                                          >
                                            Delete
                                          </button>
                                        </div>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}
      {/* Add File/Folder Drawer */}
      <Drawer
        open={showAddDrawer}
        onClose={handleCloseAddDrawer}
        title="Add File/Folder"
      >
        <div className="space-y-4">
          {openChooseFile && (
            <form>
              <Select
                label="Choose a folder (optional)"
                onChange={(e) => {
                  setSelectedFolder(e.target.value);
                }}
                className="mb-2"
              >
                <option value="">Select a folder</option>
                {folderList.map((folder) => (
                  <option key={folder.name} value={folder.name}>
                    {folder.name}
                  </option>
                ))}
              </Select>
              <Tooltip
                content="Optional if you are uploading to the root of the bucket"
                placement="right"
                style={{ bgcolor: 'auto' }}
              >
                <BsInfoCircle size={10} className="my-2 cursor-pointer" />
              </Tooltip>
              <label
                className={`inline-flex cursor-pointer items-center rounded-md ${selectedFiles.length > 0 ? 'bg-white text-primaryBlue' : 'bg-primaryBlue text-white'} px-3 py-2 text-sm font-medium  hover:bg-blue-500 hover:text-white`}
              >
                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  multiple
                />
                {selectedFiles.length > 0 ? 'Change files' : 'Choose files'}
              </label>
              {selectedFiles.length > 0 && (
                <Button
                  size="sm"
                  className="bg-primaryBlue hover:bg-blue-800 text-white disabled:opacity-50 cursor-pointer py-2 px-3 mt-4"
                  onClick={handleUpload}
                  disabled={selectedFiles.length === 0 || uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              )}

              {selectedFiles.length > 0 && (
                <div className="mt-2 text-xs text-gray-500">
                  {selectedFiles.length} file(s) selected:
                  <ul className="list-disc ml-4">
                    {selectedFiles.map((f, i) => (
                      <li key={i}>{f.name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </form>
          )}
          {openCreateFolder && (
            <form onSubmit={(e) => handleCreateFolder(e)} className="space-y-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Folder Name
              </label>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primaryBlue focus:ring focus:ring-primaryBlue focus:ring-opacity-50"
              />
              <button
                type="submit"
                className="rounded bg-primaryBlue px-3 py-2 text-sm text-white hover:bg-blue-600"
                disabled={creatingFolder}
              >
                {creatingFolder ? 'Creating...' : 'Create Folder'}
              </button>
            </form>
          )}
        </div>
      </Drawer>

      {/* Preview Drawer */}
      <Drawer
        open={!!previewKey}
        onClose={() => setPreviewKey(null)}
        title={`Preview: ${previewKey || ''}`}
      >
        {!isFetchingPreview ? (
          <div className="space-y-4">
            {/* Image */}
            {previewMime?.startsWith('image/') && (
              <img
                src={previewUrl}
                alt={previewFilename}
                style={{ maxWidth: '100%' }}
              />
            )}

            {/* PDF via iframe (quick inline preview) */}
            {previewMime === 'application/pdf' && (
              <iframe
                title={previewFilename}
                src={previewUrl}
                style={{ width: '100%', minHeight: '500px' }}
              />
            )}

            {/* Microsoft Office (doc/docx, xls/xlsx, ppt/pptx) via Office Web Viewer */}
            {[
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
              'application/msword', // .doc (legacy)
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
              'application/vnd.ms-excel', // .xls (legacy)
              'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
              'application/vnd.ms-powerpoint', // .ppt (legacy)
            ].includes(previewMime) && (
              <>
                <iframe
                  title={previewFilename}
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewUrl)}`}
                  style={{ width: '100%', minHeight: '400px' }}
                />
                <p className="text-sm text-gray-700">
                  Preview for {previewFilename} isn't supported inline. Download
                  to open on your machine.
                </p>
              </>
            )}

            <div className="flex justify-end">
              <button
                onClick={() => handleDownload(previewKey)}
                className="rounded bg-primaryBlue px-3 py-2 text-sm text-white hover:bg-blue-600"
              >
                {downloadingKey === previewKey ? 'Downloading...' : 'Download'}
              </button>
            </div>
          </div>
        ) : (
          <CardSkeleton />
        )}
      </Drawer>

      {/* Delete confirmation */}
      <ConfirmDeleteModal
        open={!!deleteTarget}
        title={isFolderName ? 'Delete Folder' : 'Delete Object'}
        name={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={
          isFolderName ? () => handleDeleteFolder(deleteTarget) : handleDelete
        }
        description={`To confirm deletion, type the ${isFolderName ? 'folder' : 'object'} name exactly`}
        isDeleting={isDeleting}
      />
    </div>
  );
}